import { DatePicker, DatePickerProps, Segmented } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { menteeObjectiveProps, USER_ROLES } from '../../../types';
import { getAllObjectives } from '../../../services/objectiveServices';
import {
  Statuses,
  convertNameToPath,
  getUserProfilesFromStorage,
} from '../../../helper';
import { Paths } from '../../../routes';
import ViewUserTooltip from '../../../components/view-user-tooltip';
import { NotificationAlert, Table } from '../../../components';
import { menteeObjectivescolumns } from '../../mentor/mentee-objectives/columns';
import { useAtomValue } from 'jotai';
import { userAtom } from '../../../store/atom';
import UserProfile from '../../../components/profile/UserProfile';
import { getAnnualCycleById } from '../../../services/annualServices';
import { getQuarter } from '../../../services/reviewsServices';

const ObjectiveDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<menteeObjectiveProps[]>([]);
  const [filtered, setFiltered] = useState<menteeObjectiveProps[]>([]);
  const { selectedMentee } = useAtomValue(userAtom);
  const myRef = useRef<any>();
  const [profilePicUrl, setProfilePicUrl] = useState<string | null>(null);
  const { selectedCycleId, isQuarter } = location.state || {};
  const [selectedYear, setSelectedYear] = useState<dayjs.Dayjs | null>(null);

  // Apply year filter function - memoized with useCallback
  const applyYearFilter = useCallback(
    (objectivesData: menteeObjectiveProps[], date: dayjs.Dayjs | null) => {
      if (!date || !objectivesData.length) {
        setFiltered(objectivesData);
        return;
      }

      const filter = objectivesData.filter(
        (i) =>
          dayjs(i.startDate).format('YYYY') === dayjs(date).format('YYYY') ||
          dayjs(i.endDate).format('YYYY') === dayjs(date).format('YYYY') ||
          (dayjs(date).isAfter(dayjs(i.startDate)) &&
            dayjs(date).isBefore(dayjs(i.endDate)))
      );
      setFiltered(filter);
    },
    []
  );

  // Fetch annual cycle details to get the year
  useEffect(() => {
    const fetchCycleDetails = async () => {
      if (!selectedCycleId && !isQuarter) {
        setSelectedYear(dayjs());
        return;
      }

      let data: any;
      let error: any;

      try {
        if (isQuarter) {
          const { data: quarterData, error: quarterError } = await getQuarter(
            selectedCycleId,
            'hr'
          );
          data = quarterData;
          error = quarterError;
        } else {
          const { data: annualData, error: annualError } =
            await getAnnualCycleById(selectedCycleId, 'hr');
          data = annualData;
          error = annualError;
        }
        if (error) {
          myRef.current?.call('error', error.message);
          setSelectedYear(dayjs());
          return;
        }

        if (data?.year) {
          setSelectedYear(dayjs(`${data.year}-01-01`));
        } else {
          setSelectedYear(dayjs());
        }
      } catch (error) {
        myRef.current?.call('error', 'Failed to fetch annual cycle details');
        setSelectedYear(dayjs());
      }
    };

    fetchCycleDetails();
  }, [selectedCycleId, isQuarter]);

  // Fetch objectives
  useEffect(() => {
    const fetchObjectives = async () => {
      if (!selectedMentee?.id) return;

      setLoading(true);
      try {
        const { data, error } = await getAllObjectives(selectedMentee.id, 'hr');
        if (error) {
          myRef.current?.call('error', error.message);
          setLoading(false);
          return;
        }

        const filteredData =
          data?.filter(
            (i) => i.status.toLowerCase() !== Statuses.Delete.toLowerCase()
          ) || [];

        setData(filteredData);

        if (selectedYear) {
          applyYearFilter(filteredData, selectedYear);
        } else {
          setFiltered(filteredData);
        }
      } catch (error) {
        myRef.current?.call('error', 'Failed to fetch objectives');
      } finally {
        setLoading(false);
      }
    };

    fetchObjectives();
  }, [selectedMentee?.id, selectedYear, applyYearFilter]);

  // Fetch profile picture
  useEffect(() => {
    const fetchProfilePic = async () => {
      if (!selectedMentee?.id) return;

      try {
        const res = await getUserProfilesFromStorage();
        const profile = res.data.find((u: any) => u.id === selectedMentee.id);

        if (profile?.file instanceof Blob) {
          const objectUrl = URL.createObjectURL(profile.file);
          setProfilePicUrl(objectUrl);

          // Clean up object URL when component unmounts or profile changes
          return () => URL.revokeObjectURL(objectUrl);
        }
      } catch (error) {
        myRef.current?.call('error', 'Failed to fetch profile picture');
      }
    };

    fetchProfilePic();
  }, [selectedMentee?.id]);

  // Date change handler
  const dateChange: DatePickerProps['onChange'] = useCallback(
    (date: dayjs.Dayjs | null) => {
      setLoading(true);
      setSelectedYear(date);
      applyYearFilter(data, date);
      setLoading(false);
    },
    [data, applyYearFilter]
  );

  // Navigation handler
  const handleNav = useCallback(
    (val: string) => {
      if (val === 'Quarterly Feedback' && selectedMentee?.name) {
        navigate(
          `${Paths.QuarterlyReview}/${convertNameToPath(selectedMentee.name)}`,
          { state: { from: 'hr' } }
        );
      }
    },
    [navigate, selectedMentee?.name]
  );

  if (!selectedMentee) {
    return <div>No mentee selected</div>;
  }

  return (
    <div className="flex flex-col gap-y-[10px]">
      <div className="flex py-[10px] pl-[10px]">
        <div className="text-[20px] font-[500]">
          <ViewUserTooltip
            user={{
              name: selectedMentee.name,
              designation: selectedMentee.designation,
              tier: selectedMentee.tier,
              mentor: {
                name: selectedMentee.mentor.name,
                designation: selectedMentee.mentor.designation,
                tier: selectedMentee.mentor.tier,
              },
              profilePic: profilePicUrl || '',
            }}
          />
        </div>
      </div>

      <div className="flex py-[10px] pl-[10px]">
        <div className="text-[20px] font-[500]">Objectives Dashboard</div>
        <div className="ml-auto">
          <Segmented
            options={['Objectives Dashboard', 'Quarterly Feedback']}
            defaultValue="Objectives Dashboard"
            onChange={handleNav}
          />
          <DatePicker
            onChange={dateChange}
            picker="year"
            className="ml-3 w-fit"
            placeholder="Year"
            value={selectedYear}
          />
        </div>
      </div>

      <Table
        columns={menteeObjectivescolumns(() => {}, USER_ROLES.HR)}
        data={filtered}
        errorFetchingData={false}
        loading={loading}
        rowKey="id"
      />

      <UserProfile
        type="hr"
        userId={selectedMentee.id}
        designation={selectedMentee.designation || ''}
        tier={selectedMentee.tier ?? ''}
      />
      <NotificationAlert ref={myRef} />
    </div>
  );
};

export default ObjectiveDashboard;
